import ThinkingLottie from '@/assets/lottie/thinking.json';
import { useAdminContext } from '@/contexts/AdminContext';
import {
    EVENT_TYPES, MessageType,
    ROLES,
    TOOL_NAME,
    ToolCompleted
} from '@common/api/agentService';
import FileItemComp from '@common/components/FileItem';
import Icon from '@common/components/Icon';
import { Button } from '@common/index';
import cls from 'classnames';
import Lottie from 'lottie-react';
import React, { useState } from 'react';
import { Markdown } from '../Markdown';
import * as Artifact from './Artifact';
import Error from './Error';
import { SourcesSummary } from './Link';
import styles from './Message.module.scss';
import * as Tool from './Tool';

const HIDDEN_TOOLS = ['message']

interface MessageProps {
    messages: MessageType[];
    setCurrentResultId?: (id: string) => void;
    isGenerating: boolean;
}

export const AssistantMessage = ({message}: {message: MessageType}) => {
    const { modal } = useAdminContext();
    switch (message.event_type) {
        case EVENT_TYPES.SYSTEM_MESSAGE_DELTA:
            return (
                <div className={`${styles.message} ${styles.assistantMessage}`}>
                    <Markdown content={message.content} />
                </div>
            )
        case EVENT_TYPES.ARTIFACT_COMPLETED:
            return (
                <div className={`${styles.message} ${styles.assistantMessage}`}>
                    <Markdown content={message.content} />
                </div>
            );
        case EVENT_TYPES.TOOL_STARTED:
            if(HIDDEN_TOOLS.includes(message.tool_name)) {
                return null;
            }
            return (
                <div className={`${styles.message} ${styles.assistantMessage}`}>
                    <Tool.Started message={message} />
                </div>
            );

        case EVENT_TYPES.TOOL_SUMMARY_DELTA:
            return (
                <div className={styles.toolSummary}>
                    <Tool.Summary message={message} />
                </div>
            )
        case EVENT_TYPES.ARTIFACT_CREATED:
            return (
                <div className={`${styles.message} ${styles.assistantMessage}`}>
                    <Artifact.Created message={message} />
                </div>
            );
        case EVENT_TYPES.ERROR:
            return (
                <Error
                    message={message}
                />
            );
        case EVENT_TYPES.ACTION_BUTTON:
            return (
                <div className={`${styles.message} ${styles.assistantMessage}`}>
                    <p style={{marginBottom: 12}}>
                        Your plan has expired.
                    </p>
                    <Button onClick={() => modal.openModal('pricing')} style={{flexShrink: 0 , width: 'fit-content'}}>
                        Upgrade
                    </Button>
                </div>
                
            );
        default:
            return null;
    }
}

export const MessageItem = ({
    message, index, isCompleted
}: {message: MessageType, index: number, isCompleted?: boolean}) => {
    switch (message.role) {
        case 'user':
            return (
                <div className={cls(styles.message)}>
                    {message?.files?.map((file: any) => {
                        const url = file.url || file.oss_url;
                        const name = url.split('/').pop() || file.name;
                        return (
                            <div key={`${name}-${index}`} className={cls(styles.messageImageContainer, styles.userMessageFiles)}>
                                {file.file_type?.startsWith('image/') ? (
                                    <img src={url} alt={name} />
                                ) : (
                                    <FileItemComp
                                        fileItem={
                                        {
                                            name,
                                            file_type: file.file_type,
                                            url,
                                        }
                                    }
                                    />
                                )}
                            </div>
                        )
                    })}
                    {message.content ? (
                        <div key={`content-${index}`} className={cls(styles.message, styles.userMessage)}>
                            <div className={styles.messageContent}>
                                {message.content}
                            </div>
                        </div>
                    ) : null}
                </div>
            );
        default:
            return <AssistantMessage message={message} />;
    }
};

// 将消息转换为二维数组，根据 agent.start 和 agent.completed 进行分组
const convertMessagesToGroups = (messages: MessageType[]) => {
    const groups = [];
    let currentGroup: MessageType[] = [];
    
    for (const message of messages) {
        if (message.event_type === EVENT_TYPES.AGENT_START) {
            // 如果当前组不为空，先保存当前组
            if (currentGroup.length > 0) {
                groups.push(currentGroup);
            }
            // 开始新的 agent 组
            currentGroup = [message];
        } else if (message.event_type === EVENT_TYPES.AGENT_COMPLETED) {
            // agent 完成，将消息加入当前组并结束当前组
            currentGroup.push(message);
            groups.push(currentGroup);
            currentGroup = [];
        } else {
            // 其他消息类型
            if (currentGroup.length > 0) {
                // 如果在 agent 组内，加入当前组
                currentGroup.push(message);
            } else {
                // 如果不在 agent 组内，作为独立消息
                groups.push(message);
            }
        }
    }
    
    // 处理最后一个未完成的组
    if (currentGroup.length > 0) {
        groups.push(currentGroup);
    }
    
    return groups;
};

export const Message: React.FC<MessageProps> = React.memo(({
    messages, isGenerating, setCurrentResultId
}) => {
    // 找到最后一个 USER_MESSAGE 的索引
    let lastUserMsgIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].event_type === EVENT_TYPES.USER_MESSAGE) {
            lastUserMsgIndex = i;
            break;
        }
    }
    // 从最后一个用户消息开始的所有消息才是 latestGroup
    const latestGroup = lastUserMsgIndex !== -1 ? messages.slice(lastUserMsgIndex) : [];
    const isDeepThinking = latestGroup.some(msg => msg.role === ROLES.TOOL)
                            && !latestGroup.some(msg => msg.event_type === EVENT_TYPES.LOOP_COMPLETED) ;

    return (
        messages.length > 0 && (
        <div className={styles.messagesContainer}>
            <AgentModeMessage isGenerating={isGenerating} messages={messages} />
            {isGenerating && !isDeepThinking ? (
                <div className={styles.message}>
                    {/* <Logo size="20px" style={{marginBottom: 12}} /> */}
                    <div className={styles.thinking}>
                        <Lottie 
                            animationData={ThinkingLottie}
                            loop={true}
                            autoplay={true}
                            style={{ 
                                width: '18px', 
                                height: '18px',
                            }}
                        />
                        Thinking
                    </div>
                </div>
            ) : null}
        </div>
        )
    );
});


export const ChatModeMessage: React.FC<MessageProps> = React.memo(({ messages, setCurrentResultId }) => {
    const tools: ToolCompleted[] = messages.filter(
        msg => msg.event_type === EVENT_TYPES.TOOL_COMPLETED 
        && msg.tool_name === TOOL_NAME.GOOGLE_SEARCH
    ) as ToolCompleted[];
    const excludeTools = messages.filter(msg => !msg.event_type.includes('tool'))
    const links = tools.map((tool) => tool.output_struct?.results || []).flat();
    const isCompleted = messages.some(msg => msg.event_type === EVENT_TYPES.AGENT_COMPLETED);
    const openSource = () => {
        setCurrentResultId?.(tools?.[0]?.tool_run_id)
    }
    return (
        <>
            {excludeTools.map((msg,index) => <MessageItem message={msg} index={index} key={`${msg.event_type}-${index}`} />)}
            {isCompleted && links.length ? <div className={styles.source} onClick={openSource}><SourcesSummary links={links} /></div> : null}
        </>
    )
})

const ThinkingBlock = ({ messages, isGenerating }: { messages: MessageType[], isGenerating: boolean }) => {
    const [isCollapsed, setIsCollapsed] = useState(true);
    
    const loopCompletedMsg = messages.find(msg => msg.event_type === EVENT_TYPES.LOOP_COMPLETED);
    const thinkingTime = loopCompletedMsg?.time_consume || 0;
    const isFinished = !!loopCompletedMsg;
    const showSpinner = !isFinished && isGenerating;

    // Filter messages for display inside thinking container
    const displayMessages = messages.filter(m => (
        m.event_type === EVENT_TYPES.SYSTEM_MESSAGE_DELTA
        || (m.event_type === EVENT_TYPES.TOOL_STARTED && !HIDDEN_TOOLS.includes(m.tool_name))
    ));

    const toolCount = displayMessages.filter(msg => msg.event_type === EVENT_TYPES.TOOL_STARTED).length;
    const roundMessage = [...messages].find(msg => (
        msg.event_type === EVENT_TYPES.SYSTEM_MESSAGE_DELTA
        || msg.event_type === EVENT_TYPES.AGENT_START
        || msg.event_type === EVENT_TYPES.TOOL_MESSAGE
    ));

    return (
        <div className={styles.thinkingContainer}>
            <div
                className={styles.thinkingHeader}
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                {showSpinner ? (
                    <div className={styles.thinkingTitle}>
                        <Lottie 
                            animationData={ThinkingLottie}
                            loop={true}
                            autoplay={true}
                            style={{ 
                                width: '18px', 
                                height: '18px',
                            }}
                        />
                        {roundMessage?.content || 'Thinking'}
                    </div>
                ) : (
                    <div className={styles.thinkingTitle}>
                        Thought for 
                        {' '}
                        {thinkingTime}
                        {' '}
                        seconds
                    </div>
                )}
                <div 
                    style={{transform: isCollapsed ? 'rotate(270deg)' : 'rotate(0deg)',}}
                    className={styles.toggle}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M5.15128 9.84839C4.68265 9.37976 4.68265 8.62015 5.15128 8.15152C5.6199 7.68289 6.37952 7.68289 6.84815 8.15152L12.8481 14.1515C13.3168 14.6201 13.3168 15.3797 12.8481 15.8484C12.3795 16.317 11.6199 16.317 11.1513 15.8484L5.15128 9.84839Z" fill="#1A162F" />
                        <path d="M11.1513 15.8484C10.6826 15.3797 10.6826 14.6201 11.1513 14.1515L17.1512 8.15152C17.6199 7.68289 18.3795 7.68289 18.8481 8.15152C19.3167 8.62015 19.3167 9.37976 18.8481 9.84839L12.8481 15.8484C12.3795 16.317 11.6199 16.317 11.1513 15.8484Z" fill="#1A162F" />
                    </svg>
                </div>
            </div>
            {!showSpinner && isCollapsed
                ? (
                    <span style={{color: '#666F8D', fontSize: 14}}>
                        {toolCount}
                        {' '}
                        tools used
                    </span>
                ): (
                    <div
                        className={styles.thinkingContent}
                        style={{maxHeight: isCollapsed ? '100px' : 'fit-content'}}
                    >
                        {isCollapsed ? <div className={styles.overlay}></div> : null}
                        {displayMessages.map((m, mIndex) => {
                            const isLatest = showSpinner && mIndex === 0;
                            const isLastItem = mIndex === 0;
                            const isFirstItem = mIndex === displayMessages.length - 1;

                            return (
                                <div className={styles.thinkingItem} key={`think-item-${mIndex}`}>
                                    <div className={styles.timelineNode}>
                                        {!isFirstItem && (
                                            <div 
                                                className={styles.timelineLine} 
                                                style={{top: 0, height: '50%'}} 
                                            />
                                        )}
                                        {!isLastItem && (
                                            <div 
                                                className={styles.timelineLine} 
                                                style={{top: '50%', bottom: '-16px'}} 
                                            />
                                        )}
                                        <div className={styles.timelineIcon}>
                                            <Icon 
                                                name={isLatest ? "loading" : "check"} 
                                                size={18} 
                                                className={isLatest ? styles.loadingIcon : ''}
                                            />
                                        </div>
                                    </div>
                                    
                                    <AssistantMessage message={m} />
                                        
                                </div>
                            );
                        })}
                    </div>
                )}
        </div>
    );
};

export const AgentModeMessage: React.FC<MessageProps> = React.memo(({ messages, isGenerating }) => {
    const grouped: MessageType[][] = [];
    let currentGroup: MessageType[] | null = null;

    for (let i = 0; i < messages.length; i++) {
        const msg = messages[i];
        const evt = msg.event_type as string | undefined;

        if (evt === EVENT_TYPES.USER_MESSAGE) {
            // 如果已有分组在进行，先收束上一个分组
            if (currentGroup) {
                grouped.push(currentGroup);
            }
            currentGroup = [msg];
            continue;
        }else{
            currentGroup?.push(msg);
        }
    }

    if (currentGroup) {
        grouped.push(currentGroup);
    }
    return (
        <>
            {grouped.map((item, index) => {
                const userMsg = item[0]
                const otherMsgs = item.slice(1);

                // Find the split point based on LOOP_COMPLETED
                let splitIndex = -1;
                for (let i = otherMsgs.length - 1; i >= 0; i--) {
                    if (otherMsgs[i].event_type === EVENT_TYPES.LOOP_COMPLETED) {
                        splitIndex = i;
                        break;
                    }
                    if(otherMsgs[i].event_type === EVENT_TYPES.ACTION_BUTTON){
                        splitIndex = i - 1;
                        break;
                    }
                }

                // If loop completed found, everything up to it (inclusive) is thinking.
                // If not found, all are thinking (unless we want to assume finished without loop completed?)
                // Assuming default behavior: if no loop completed, all in thinking.
                const thinkingMsgs = splitIndex !== -1 ? otherMsgs.slice(0, splitIndex + 1) : otherMsgs;
                const resultMsgs = splitIndex !== -1 ? otherMsgs.slice(splitIndex + 1) : [];
                return (
                    <React.Fragment key={`agent-round-${index}`}>
                        <MessageItem
                            message={userMsg}
                            index={index}
                            key={`${userMsg.event_type}-${index}`}
                        />
                        {thinkingMsgs.find(msg => msg.event_type === EVENT_TYPES.TOOL_STARTED) && (
                            <ThinkingBlock 
                                messages={thinkingMsgs.reverse()} 
                                isGenerating={isGenerating} 
                            />
                        )}
                        {resultMsgs.map((msg, rIndex) => (
                            <MessageItem 
                                message={msg} 
                                index={rIndex} 
                                key={`result-${index}-${rIndex}`} 
                            />
                        ))}
                    </React.Fragment>
                );

            })}
        </>
    );
})
